import { flatten } from 'utils';
import { useMemo } from 'react';

import messagesInEnglish from '../../lang/en.json';
import messagesInEnglishAU from '../../lang/en-AU.json';
import messagesInEnglishCA from '../../lang/en-CA.json';
import messagesInEnglishGB from '../../lang/en-GB.json';
import messagesInEnglishIE from '../../lang/en-IE.json';
import messagesInEnglishUS from '../../lang/en-US.json';
import messagesInGermanDE from '../../lang/de-DE.json';
import messagesInSpanishES from '../../lang/es-ES.json';
import messagesInFrenchCA from '../../lang/fr-CA.json';
import messagesInFrenchFR from '../../lang/fr-FR.json';
import messagesInHungarianHU from '../../lang/hu-HU.json';
import messagesInItalianIT from '../../lang/it-IT.json';
import messagesInNorwegianNB from '../../lang/nb-NO.json';
import messagesInDutchNL from '../../lang/nl-NL.json';
import messagesInPolishPL from '../../lang/pl.json';
import messagesInPortuguesePT from '../../lang/pt-PT.json';
import messagesInSlovakSK from '../../lang/sk-SK.json';

/**
 * Region-less on purpose: hardcoding `en-US` here would pin US date order and a 12-hour clock on a
 * London user whose host passed no locale.
 */
export const DEFAULT_LOCALE = 'en';

/**
 * Canonical BCP-47 casing for a host-supplied tag — `en_us` and `EN-US` both become `en-US` — with any
 * POSIX codeset or modifier (`en_GB.UTF-8`, `de_DE@euro`) stripped so the region survives. Subtag casing
 * per BCP 47: language lowercase, 4-letter script Titlecase, region uppercase — then `Intl`'s own
 * canonicalisation where available. Same rules as `@hqo/date-time`'s `normalizeLocale`, which this miniapp does
 * not depend on.
 */
// `Intl.getCanonicalLocales` is ES2020; this project's `lib` predates it, so it is reached through a narrow type.
type IntlWithCanonicalLocales = typeof Intl & { getCanonicalLocales?: (locales: string) => string[] };

const canonicalLocale = (input: string): string | null => {
  const [language, ...subtags] = input
    .trim()
    .replace(/_/g, '-')
    .replace(/[.@].*$/, '')
    .split('-');

  const cased = [
    language.toLowerCase(),
    ...subtags.map((subtag) => {
      if (subtag.length === 4) {
        return subtag.charAt(0).toUpperCase() + subtag.slice(1).toLowerCase();
      }

      if (subtag.length === 2 || /^\d{3}$/.test(subtag)) {
        return subtag.toUpperCase();
      }

      return subtag.toLowerCase();
    }),
  ].join('-');

  // A tag `Intl` rejects (`de--AT`, `en-US-foo`) must never be reported as the formatting locale: react-intl would
  // hand it to `Intl` and the first number or date would throw. `null` sends the caller to the default instead.
  // Where the runtime has it, `Intl` also yields the full canonical form (`en-US-POSIX` -> `en-US-u-va-posix`).
  try {
    const { getCanonicalLocales } = Intl as IntlWithCanonicalLocales;
    if (getCanonicalLocales) {
      return getCanonicalLocales(cased)[0] ?? null;
    }
    Intl.DateTimeFormat.supportedLocalesOf(cased);

    return cased;
  } catch {
    return null;
  }
};

/**
 * Every catalog this miniapp ships, keyed by lowercased BCP-47 tag. Static named imports (rather than
 * a dynamic `import()`) keep resolution synchronous and the bundle limited to exactly these locales.
 *
 * Add a locale by adding a row here AND a `lang/<tag>.json`. `translations.spec.ts` fails if the two
 * ever disagree, so neither half can be forgotten.
 */
const rawMessagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.lutron],
  ['en-au', messagesInEnglishAU.lutron],
  ['en-ca', messagesInEnglishCA.lutron],
  ['en-gb', messagesInEnglishGB.lutron],
  ['en-ie', messagesInEnglishIE.lutron],
  ['en-us', messagesInEnglishUS.lutron],
  ['de-de', messagesInGermanDE.lutron],
  ['es-es', messagesInSpanishES.lutron],
  // Bare `fr` is declared EXPLICITLY as France. French is the one language here with two regional
  // catalogs, so leaving bare `fr` to derivation would make its meaning depend on this Map's
  // insertion order — reordering these rows alphabetically would silently hand `fr` to Quebec.
  ['fr', messagesInFrenchFR.lutron],
  ['fr-ca', messagesInFrenchCA.lutron],
  ['fr-fr', messagesInFrenchFR.lutron],
  ['hu-hu', messagesInHungarianHU.lutron],
  ['it-it', messagesInItalianIT.lutron],
  ['nb-no', messagesInNorwegianNB.lutron],
  ['nl-nl', messagesInDutchNL.lutron],
  ['pl', messagesInPolishPL.lutron],
  ['pt-pt', messagesInPortuguesePT.lutron],
  ['sk-sk', messagesInSlovakSK.lutron],
]);

/**
 * Fills in a bare-language entry for any language that has exactly one regional catalog, so a host
 * passing `de` (no region) gets German rather than falling all the way back to English.
 *
 * A tag declared explicitly above — `en`, `fr`, `pl` — is never overwritten. That matters: any
 * language with MORE than one regional catalog must be declared explicitly, because derivation would
 * otherwise resolve it by this Map's insertion order, making the result depend on row ordering rather
 * than on a decision anyone made.
 */
const withDerivedBaseLocales = (source: Map<string, Record<string, unknown>>) => {
  const derived = new Map(source);

  source.forEach((messages, tag) => {
    const [language] = tag.split('-');

    if (language !== tag && !derived.has(language)) {
      derived.set(language, messages);
    }
  });

  return derived;
};

const englishBase = flatten(messagesInEnglish.lutron) as Record<string, string>;

/**
 * Each catalog is merged OVER English rather than used on its own.
 *
 * This is what lets a regional file be sparse — the five English regionals ship empty, because their
 * job is to make the TAG resolve (so dates/numbers go regional) and there is no spelling divergence
 * to carry. It also means a partially-translated catalog renders its translated keys and falls back
 * to English for the rest, instead of rendering a raw message id.
 */
const messagesMap = new Map<string, Record<string, string>>();
withDerivedBaseLocales(rawMessagesMap).forEach((messages, tag) => {
  messagesMap.set(tag, { ...englishBase, ...(flatten(messages) as Record<string, string>) });
});

/**
 * Picks the catalog to render with, and the locale tag to report to `react-intl`.
 *
 * Returns `[messagesKey, locale]`, and the two are deliberately different things:
 *
 * - **`locale`** is CANONICAL BCP-47, via `canonicalLocale` — so `en_us` and `EN-US` both report
 *   `en-US`. This value becomes `useIntl().locale` and flows straight into the formatters (the points
 *   balance's digit grouping among them), so it has to be a tag `Intl` is happy to receive rather than
 *   whatever casing the host happened to send.
 * - **`messagesKey`** is that canonical tag lowercased, because that is how the catalog Map is keyed.
 *   Deriving it from the canonical form rather than from the raw input is deliberate: the
 *   underscore-to-hyphen conversion then happens in exactly one place instead of two that can drift.
 *
 * The two also differ in a second, load-bearing way: a regional tag with no catalog of its own falls
 * back to its LANGUAGE catalog but still reports the full regional tag. `de-AT` renders the `de` words
 * while reporting `de-AT`, so Austrian date conventions survive. Collapsing the two would throw the
 * region away and silently hand `de-AT` German-of-Germany formatting.
 */
export const getAvailableLocale = (userLocale: string): [string, string] => {
  // Guard before normalizing: "the host told us nothing" stays the region-less default rather than
  // becoming a regional choice.
  if (!userLocale || !userLocale.trim()) {
    return [DEFAULT_LOCALE, DEFAULT_LOCALE];
  }

  const canonical = canonicalLocale(userLocale);

  if (!canonical) {
    return [DEFAULT_LOCALE, DEFAULT_LOCALE];
  }
  const lookupKey = canonical.toLowerCase();

  if (messagesMap.has(lookupKey)) {
    return [lookupKey, canonical];
  }

  // An extended tag (`fr-CA-u-ca-gregory`, `de-AT-x-pos`) misses the map on its full form; try its
  // language-REGION catalog before the bare language, or `fr-CA-…` would get France's catalog. Subtags stop at
  // the first singleton (`u`, `t`, `x`), which opens an extension; a script subtag (`zh-hant-tw`) is skipped.
  const [language, ...subtags] = lookupKey.split('-');
  const extensionAt = subtags.findIndex((subtag) => subtag.length === 1);
  const beforeExtensions = extensionAt === -1 ? subtags : subtags.slice(0, extensionAt);
  const region = beforeExtensions.find((subtag) => subtag.length === 2 || /^\d{3}$/.test(subtag));
  const regionalKey = region ? `${language}-${region}` : undefined;

  if (regionalKey && messagesMap.has(regionalKey)) {
    return [regionalKey, canonical];
  }

  if (messagesMap.has(language)) {
    return [language, canonical];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData =>
  useMemo(() => {
    const [messagesKey, locale] = getAvailableLocale(userLocale);

    return {
      locale,
      // `getAvailableLocale` only ever returns a key it found in the map (or DEFAULT_LOCALE, which is
      // always present), so this cannot miss — but fall back rather than assert, so a future edit to
      // the map can't turn a missing catalog into a blank render.
      messages: messagesMap.get(messagesKey) ?? messagesMap.get(DEFAULT_LOCALE)!,
    };
  }, [userLocale]);
